import { GazeData } from "types/testTypes";

export type AxisOrientation = "horizontal" | "vertical" | "indeterminate";
export type NystagmusDirection = "left" | "right" | "up" | "down" | "indeterminate";

export interface NystagmusDetectionCriteria {
  hasEnoughData: boolean;
  amplitudeAboveThreshold: boolean;
  amplitudeBeatsNoise: boolean;
  minimumCyclesMet: boolean;
  frequencyWithinPhysiologicalRange: boolean;
  slowPhaseVelocityPresent: boolean;
}

export interface NystagmusSummary {
  detected: boolean;
  hasEnoughData: boolean;
  dominantAxis: AxisOrientation;
  direction: NystagmusDirection;
  amplitude: number | null;
  amplitudeToNoiseRatio: number | null;
  frequencyHz: number | null;
  slowPhaseVelocity: number | null;
  durationSeconds: number | null;
  windowStartSeconds: number | null;
  windowEndSeconds: number | null;
  sampleCount: number;
  zeroCrossings: number | null;
  detectionRiskScore: number;
  windowsEvaluated: number;
  detectionCriteria: NystagmusDetectionCriteria;
}

interface GazeSample {
  timestamp: number;
  x: number | null;
  y: number | null;
}

interface AxisStats {
  min: number;
  max: number;
  mean: number;
  range: number;
}

const MIN_SAMPLE_COUNT = 30;
const MIN_DURATION_SECONDS = 1.5;
const WINDOW_SECONDS = 6;
const WINDOW_STEP_SECONDS = 1;
const MAX_WINDOWS_TO_EVALUATE = 320;
const MAX_RECORDING_SECONDS_TO_SCAN = 240;
const AMPLITUDE_THRESHOLD = 0.8;
const ZERO_CROSSING_EPSILON = 0.05;
const MIN_ZERO_CROSSINGS_PER_WINDOW = 4;
const MIN_ACCEPTABLE_FREQUENCY_HZ = 0.5;
const MAX_ACCEPTABLE_FREQUENCY_HZ = 7;
const MIN_AMPLITUDE_TO_NOISE_RATIO = 2.5;
const MIN_SLOW_PHASE_VELOCITY_ABS = 0.5;
const NOISE_FLOOR = 0.01;

const round = (value: number | null, digits = 2): number | null => {
  if (value === null || Number.isNaN(value)) return null;
  return Number(value.toFixed(digits));
};

const resolveCoordinate = (point: GazeData, axis: "x" | "y"): number | null => {
  const combined = point.combinedGazePosition?.[axis];
  if (typeof combined === "number" && Number.isFinite(combined)) {
    return combined;
  }

  const left = point.leftGazePosition?.[axis];
  const right = point.rightGazePosition?.[axis];

  if (typeof left === "number" && typeof right === "number") {
    return (left + right) / 2;
  }

  if (typeof left === "number" && Number.isFinite(left)) {
    return left;
  }

  if (typeof right === "number" && Number.isFinite(right)) {
    return right;
  }

  return null;
};

const createSamples = (gazeDatas: GazeData[]): GazeSample[] =>
  gazeDatas
    .map((point, index) => {
      // The headset records the sample time at the top level as `Timestamp`, in
      // deciseconds (elapsed seconds × 10). All timing math below works in
      // milliseconds, so convert deciseconds → ms (×100). Fall back to a 10 Hz
      // (100 ms/sample) assumption only when the timestamp is missing.
      const timestampCandidate = point.Timestamp != null ? point.Timestamp * 100 : index * 100;

      const timestamp =
        typeof timestampCandidate === "number" && Number.isFinite(timestampCandidate)
          ? timestampCandidate
          : index * 100;

      return {
        timestamp,
        x: resolveCoordinate(point, "x"),
        y: resolveCoordinate(point, "y"),
      };
    })
    .filter((sample) => Number.isFinite(sample.timestamp));

const trimSamplesForScan = (samples: GazeSample[]): GazeSample[] => {
  if (!samples.length) return samples;
  const firstTimestamp = samples[0].timestamp;
  const lastTimestamp = samples[samples.length - 1].timestamp;
  const totalSeconds = (lastTimestamp - firstTimestamp) / 1000;
  if (totalSeconds <= MAX_RECORDING_SECONDS_TO_SCAN) {
    return samples;
  }
  const cutoffTimestamp = lastTimestamp - MAX_RECORDING_SECONDS_TO_SCAN * 1000;
  const trimmed = samples.filter((sample) => sample.timestamp >= cutoffTimestamp);
  return trimmed.length ? trimmed : samples;
};

const extractAxisValues = (samples: GazeSample[], axis: "x" | "y"): number[] =>
  samples
    .map((sample) => sample[axis])
    .filter((value): value is number => value !== null && Number.isFinite(value));

const calculateStandardDeviation = (values: number[]): number | null => {
  if (values.length < 2) return null;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
};

const generateWindowSlices = (
  samples: GazeSample[],
  windowSeconds: number,
  stepSeconds: number,
  maxWindows: number,
): GazeSample[][] => {
  if (!samples.length) return [];
  if (samples.length < MIN_SAMPLE_COUNT) return [samples];

  const windowMs = windowSeconds * 1000;
  const stepMs = stepSeconds * 1000;
  const firstTimestamp = samples[0].timestamp;
  const lastTimestamp = samples[samples.length - 1].timestamp;
  const totalDurationMs = lastTimestamp - firstTimestamp;

  if (totalDurationMs <= windowMs) {
    return [samples];
  }

  const maxStartTimestamp = Math.max(firstTimestamp, lastTimestamp - windowMs);
  const windows: GazeSample[][] = [];
  let startTimestamp = firstTimestamp;
  let startIndex = 0;

  while (startTimestamp <= maxStartTimestamp && windows.length < maxWindows) {
    while (startIndex < samples.length && samples[startIndex].timestamp < startTimestamp) {
      startIndex += 1;
    }
    if (startIndex >= samples.length) break;

    const windowEndTimestamp = startTimestamp + windowMs;
    let endIndex = startIndex;
    while (endIndex < samples.length && samples[endIndex].timestamp <= windowEndTimestamp) {
      endIndex += 1;
    }

    const windowSamples = samples.slice(startIndex, endIndex);
    if (windowSamples.length >= MIN_SAMPLE_COUNT) {
      windows.push(windowSamples);
    }

    if (windowEndTimestamp >= lastTimestamp) {
      break;
    }

    startTimestamp += stepMs;
  }

  if (!windows.length) {
    windows.push(samples);
  } else {
    const lastWindow = windows[windows.length - 1];
    const lastWindowEnd = lastWindow[lastWindow.length - 1]?.timestamp;
    if (lastWindowEnd !== lastTimestamp && windows.length < maxWindows) {
      const trailingStartTimestamp = lastTimestamp - windowMs;
      const trailingSamples = samples.filter(
        (sample) => sample.timestamp >= trailingStartTimestamp && sample.timestamp <= lastTimestamp,
      );
      if (trailingSamples.length >= MIN_SAMPLE_COUNT) {
        windows.push(trailingSamples);
      }
    }
  }

  return windows;
};

const getAxisStats = (values: number[]): AxisStats | null => {
  if (!values.length) return null;
  let min = values[0];
  let max = values[0];
  let sum = 0;

  for (const value of values) {
    if (value < min) min = value;
    if (value > max) max = value;
    sum += value;
  }

  return {
    min,
    max,
    mean: sum / values.length,
    range: max - min,
  };
};

const countZeroCrossings = (values: number[], mean: number): number => {
  if (values.length < 2) return 0;
  let crossings = 0;
  let previous = values[0] - mean;

  for (let i = 1; i < values.length; i += 1) {
    const current = values[i] - mean;
    if (Math.abs(previous) < ZERO_CROSSING_EPSILON) {
      previous = current;
      continue;
    }

    if (Math.abs(current) < ZERO_CROSSING_EPSILON) {
      continue;
    }

    if (previous * current < 0) {
      crossings += 1;
    }

    previous = current;
  }

  return crossings;
};

const computeDirection = (axis: AxisOrientation, stats: AxisStats | null): NystagmusDirection => {
  if (!stats || axis === "indeterminate") return "indeterminate";

  const { min, max } = stats;
  if (Math.abs(max) === Math.abs(min)) return "indeterminate";

  if (axis === "horizontal") {
    return Math.abs(max) >= Math.abs(min) ? "right" : "left";
  }

  return Math.abs(max) >= Math.abs(min) ? "up" : "down";
};

const computeSlowPhaseVelocity = (
  samples: GazeSample[],
  axis: AxisOrientation,
  amplitude: number | null,
): number | null => {
  if (axis === "indeterminate") return null;

  const velocities: number[] = [];
  for (let i = 1; i < samples.length; i += 1) {
    const current = axis === "horizontal" ? samples[i].x : samples[i].y;
    const previous = axis === "horizontal" ? samples[i - 1].x : samples[i - 1].y;
    if (current === null || previous === null) continue;

    const deltaTime = (samples[i].timestamp - samples[i - 1].timestamp) / 1000;
    if (!deltaTime || deltaTime <= 0) continue;

    velocities.push((current - previous) / deltaTime);
  }

  if (!velocities.length) return null;

  const cappedVelocities =
    amplitude !== null
      ? velocities.filter((velocity) => Math.abs(velocity) <= amplitude * 2)
      : velocities;

  if (!cappedVelocities.length) return null;

  const average =
    cappedVelocities.reduce((sum, velocity) => sum + Math.abs(velocity), 0) /
    cappedVelocities.length;

  return round(average, 2);
};

const evaluateWindow = (
  samples: GazeSample[],
  baselineTimestamp: number,
): Omit<NystagmusSummary, "windowsEvaluated"> | null => {
  if (!samples.length) return null;

  const xValues = extractAxisValues(samples, "x");
  const yValues = extractAxisValues(samples, "y");

  const xStats = getAxisStats(xValues);
  const yStats = getAxisStats(yValues);

  let dominantAxis: AxisOrientation = "indeterminate";
  let dominantStats: AxisStats | null = null;

  if (xStats && yStats) {
    dominantAxis = xStats.range >= yStats.range ? "horizontal" : "vertical";
    dominantStats = dominantAxis === "horizontal" ? xStats : yStats;
  } else if (xStats) {
    dominantAxis = "horizontal";
    dominantStats = xStats;
  } else if (yStats) {
    dominantAxis = "vertical";
    dominantStats = yStats;
  }

  const amplitude = round(dominantStats?.range ?? null, 2);
  const durationSeconds =
    samples.length > 1
      ? round((samples[samples.length - 1].timestamp - samples[0].timestamp) / 1000, 2)
      : null;

  const axisValues =
    dominantAxis === "horizontal" ? xValues : dominantAxis === "vertical" ? yValues : [];

  const hasEnoughData =
    axisValues.length >= MIN_SAMPLE_COUNT && (durationSeconds ?? 0) >= MIN_DURATION_SECONDS;

  const zeroCrossings =
    dominantStats && axisValues.length ? countZeroCrossings(axisValues, dominantStats.mean) : null;

  const frequencyHz = (() => {
    if (!dominantStats || !durationSeconds || durationSeconds <= 0 || !zeroCrossings) return null;
    return round(zeroCrossings / (2 * durationSeconds), 2);
  })();

  const amplitudeToNoiseRatio = (() => {
    if (amplitude === null || !axisValues.length) return null;
    const noiseStdDev = calculateStandardDeviation(axisValues);
    if (noiseStdDev === null) return null;
    const safeStdDev = Math.max(noiseStdDev, NOISE_FLOOR);
    return round(amplitude / (safeStdDev * 2), 2);
  })();

  const slowPhaseVelocity = computeSlowPhaseVelocity(samples, dominantAxis, amplitude);

  const detectionCriteria: NystagmusDetectionCriteria = {
    hasEnoughData,
    amplitudeAboveThreshold: Boolean(amplitude && amplitude >= AMPLITUDE_THRESHOLD),
    amplitudeBeatsNoise: Boolean(
      amplitudeToNoiseRatio && amplitudeToNoiseRatio >= MIN_AMPLITUDE_TO_NOISE_RATIO,
    ),
    minimumCyclesMet: Boolean(zeroCrossings && zeroCrossings >= MIN_ZERO_CROSSINGS_PER_WINDOW),
    frequencyWithinPhysiologicalRange: Boolean(
      frequencyHz &&
      frequencyHz >= MIN_ACCEPTABLE_FREQUENCY_HZ &&
      frequencyHz <= MAX_ACCEPTABLE_FREQUENCY_HZ,
    ),
    slowPhaseVelocityPresent: Boolean(
      slowPhaseVelocity && Math.abs(slowPhaseVelocity) >= MIN_SLOW_PHASE_VELOCITY_ABS,
    ),
  };

  const satisfiedCriteria = Object.values(detectionCriteria).filter(Boolean).length;
  const criteriaCount = Object.keys(detectionCriteria).length;
  const detectionRiskScore = round(satisfiedCriteria / criteriaCount, 2) ?? 0;
  const detected = Object.values(detectionCriteria).every(Boolean);

  const windowStartSeconds = samples.length
    ? round((samples[0].timestamp - baselineTimestamp) / 1000, 2)
    : null;
  const windowEndSeconds = samples.length
    ? round((samples[samples.length - 1].timestamp - baselineTimestamp) / 1000, 2)
    : null;

  return {
    detected,
    hasEnoughData,
    dominantAxis,
    direction: computeDirection(dominantAxis, dominantStats),
    amplitude,
    amplitudeToNoiseRatio,
    frequencyHz,
    slowPhaseVelocity,
    durationSeconds,
    windowStartSeconds,
    windowEndSeconds,
    sampleCount: axisValues.length,
    zeroCrossings,
    detectionRiskScore,
    detectionCriteria,
  };
};

export const computeNystagmusSummary = (gazeDatas?: GazeData[] | null): NystagmusSummary | null => {
  if (!gazeDatas || gazeDatas.length === 0) {
    return null;
  }

  const samples = createSamples(gazeDatas);
  if (!samples.length) return null;

  const samplesToAnalyze = trimSamplesForScan(samples);
  if (!samplesToAnalyze.length) return null;

  const baselineTimestamp = samplesToAnalyze[0].timestamp;
  const windows = generateWindowSlices(
    samplesToAnalyze,
    WINDOW_SECONDS,
    WINDOW_STEP_SECONDS,
    MAX_WINDOWS_TO_EVALUATE,
  );

  if (!windows.length) return null;

  const windowSummaries = windows
    .map((windowSamples) => evaluateWindow(windowSamples, baselineTimestamp))
    .filter((summary): summary is Omit<NystagmusSummary, "windowsEvaluated"> => Boolean(summary));

  if (!windowSummaries.length) return null;

  const bestDetected = windowSummaries
    .filter((summary) => summary.detected)
    .sort((a, b) => (b.detectionRiskScore ?? 0) - (a.detectionRiskScore ?? 0))[0];

  const fallback = [...windowSummaries].sort(
    (a, b) => (b.detectionRiskScore ?? 0) - (a.detectionRiskScore ?? 0),
  )[0];

  const chosenSummary = bestDetected ?? fallback;

  if (!chosenSummary) return null;

  return {
    ...chosenSummary,
    windowsEvaluated: windowSummaries.length,
  };
};
